import _ from "lodash";
import {
    Chord,
    Note,
    NoteDuration,
    NoteVelocity,
} from "./core";
import { FULL_RANGE } from "../components/pickers/NoteRangeSelector";
import { IntervalTree } from "../utils/datastructures/IntervalTree";

interface HorizontalContainer<T> {
    getLength(): number;
    get(step: number): ContainerAtom<T>[];
    getGrid(compact?: boolean, range?: { start: number, length: number }): ContainerAtom<T>[][];
    search(start: number, end: number): ContainerAtom<T>[];
    getPPQ(): number;
    // getPickupLength(): number;
}

type NoteRangeType = {
    min: number;
    max: number;
}

export type ContainerAtom<T> = {
    step: number,
    payload: T,
    duration: NoteDuration,

    // FIXME: does it make sense to have velocity for all container types, i.e. chords?
    velocity: NoteVelocity,
};

export type ContainerEvent<T> = ContainerAtom<T> & {
    step: number
};

abstract class AbstractHorizontalContainer<T>
    implements HorizontalContainer<T> {

    /**
     * the grid ia an IntervalTree where each entry is a list of ContainerAtom<T>.
     * Access is made through searches in the tree.
     * All updates to the container's notes must be done through the tree's insert/remove methods. Specifically:
     * - the update of a note's duration must be done by removing the note and re-inserting it with the
     * new duration.
     * - since this update happens only by dragging the note's right edge, we have only one place where
     * to worry about this, making this an acceptable price.
     */
    private grid: IntervalTree<ContainerAtom<T>>;
    /**
     * PPQ defines how a NoteDuration value must be interpreted.
     * Examples:
     * - ppq = 1, a note of duration 1 is a quarter note. This is the minimal resolution this HNC supports.
     * - ppq = 4, a note of duration 1 is a sixteenth note. A NoteDuration of 4 is a quarter note.
     * - ppq = 16, a note of duration 1 is a 64th note. A NoteDuration of 16 is a quarter note.
     *
     * PPQ can be set to the value of a MIDI input midi file to simplify the conversion of MIDI files to HNCs
     * and later playback.
     * This is not a requirement, but ~it is a good practice to~ ideally we use the same PPQ value for all HNCs
     * in a project.
     */

    constructor(
        private length: number = 480 * 4 * 16,
        private ppq = 4,
        // private pickupLength = ppq * 4
    ) {
        this.length = length;
        this.grid = new IntervalTree<ContainerAtom<T>>();
        this.ppq = ppq;
    }

    getLength() {
        return this.length;
    }

    getPPQ() {
        return this.ppq;
    }

    // getPickupLength() {
    //     return this.pickupLength;
    // }

    getActiveSteps() {
        return this.grid.search(0, this.length)
            .map((ns, i) => {
                return { step: i, include: ns !== undefined }
            }).filter(({ include }) => include)
            .map(({ step }) => step);
    }

    get(step: number): ContainerAtom<T>[] {
        return this.grid.searchPoint(step)
            .map(n => n.value)
            .filter(n => n !== undefined) as ContainerAtom<T>[];
    }

    put(
        step: number,
        { payload, duration, velocity }: ContainerAtom<T>
    ) {
        if (!(duration instanceof NoteDuration)) {
            throw new Error('Duration must be an instance of NoteDuration.');
        }

        this.grid.insert(step, step + duration.value, { step, payload, duration, velocity });
    }

    replace(
        step: number,
        { payload, duration, velocity }: ContainerAtom<T>
    ) {
        this.grid.remove(step, step + duration.value, { step, payload, duration, velocity });
        this.grid.insert(step, step + duration.value, { step, payload, duration, velocity });
    }

    remove(
        step: number,
        item: ContainerAtom<T>
    ) {
        const removed = this.grid.remove(step, step + item.duration.value, { step, payload: item.payload, duration: item.duration, velocity: item.velocity } as any);
    }

    clear(step: number) {
        this.get(step).forEach(n => {
            this.grid.remove(step, step + n.duration.value, n);
        });
    }

    setLength(length: number) {
        this.length = length;
    }

    // FIXME: the access to grid should be always medieted through a window.
    getGrid(
        compact: boolean = true,
        range: { start: number, length: number } = { start: 0, length: this.length }
    ): ContainerAtom<T>[][] {
        const acc = this.grid.search(range.start, range.start + range.length)
            .map((ns) => ns.value)
            .reduce((acc, n) => {
                if (!n) return acc;
                const step = n.step;
                if (!acc.has(step)) {
                    acc.set(step, []);
                }
                acc.get(step)!.push(n);
                return acc;
            }, new Map<number, ContainerAtom<T>[]>() as Map<number, ContainerAtom<T>[]>);

        const res: ContainerAtom<T>[][] = [];
        acc.forEach((v, k) => {
            res[k] = v;
        });
        return res;
    }

    search(start: number, end: number): ContainerAtom<T>[] {
        return this.grid.search(start, end)
            .map(n => n.value)
            .filter(n => n !== undefined) as ContainerAtom<T>[];
    }
}

class HorizontalNoteContainer
    extends AbstractHorizontalContainer<Note> {
    readonly type: string = 'noteGroup';
    constructor({
        length,
        ppq,
        // pickupLength
    }: {
        length: number,
        ppq?: number,
        // pickupLength?: number
    }) {
        super(length, ppq);
    }

    put(
        step: number,
        { payload, duration, velocity }: ContainerAtom<Note>
    ): void {
        if (!Number.isInteger(payload.index) || payload.index < 0 || payload.index > 107) {
            throw new Error(`Note must be an integer between 0 and 107. Was ${payload}`);
        }
        super.put(step, { step, payload, duration, velocity });
    }

    getNoteRange(): NoteRangeType {
        const notes = this.getGrid(true).flat().map(n => n.payload.index);
        if (notes.length === 0) return FULL_RANGE;
        return {
            min: Math.min(...notes),
            max: Math.min(Math.max(...notes) + 1, FULL_RANGE.max)
        };
    }
}

class ChordContainer
    extends AbstractHorizontalContainer<Chord> {
    readonly type: string = 'chordGroup';

    constructor({
        length,
        ppq,
    }: {
        length: number,
        ppq?: number,
    }) {
        super(length, ppq);
    }
}

export {
    AbstractHorizontalContainer,
    ChordContainer,
    HorizontalNoteContainer,
    NoteRangeType,
    HorizontalContainer
};